import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { clusters as clustersApi, embeddings as embeddingsApi, projects as projectsApi } from '../lib/api.js'
import { realtime } from '../lib/realtime.js'

// --- Styles ---

const pageWrap = css`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  a { color: ${colors.textSecondary}; }
`

const headerRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md};
`

const statsBar = css`
  display: flex;
  gap: ${space.lg};
  padding: ${space.md} ${space.lg};
  background: ${colors.bgSidebar};
  border-radius: 8px;
  font-size: ${font.size.sm};
`

const statItem = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const statLabel = css`
  color: ${colors.textMuted};
  font-size: ${font.size.xs};
`

const statValue = css`
  font-weight: ${font.weight.semibold};
  color: ${colors.text};
`

const configPanel = css`
  ${card};
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`

const clusterGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${space.md};
`

const clusterCard = css`
  ${card};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    border-color: ${colors.brand};
    box-shadow: 0 2px 8px ${colors.shadow};
  }
`

const clusterLabel = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.base};
  color: ${colors.text};
  margin-bottom: ${space.xs};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const clusterMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-bottom: ${space.sm};
`

const tagChips = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const tagChip = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
`

const inlineForm = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
`

const smallInput = css`
  ${input};
  max-width: 80px;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

const progressBar = css`
  height: 4px;
  background: ${colors.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${space.sm};
`

const progressFill = css`
  height: 100%;
  background: ${colors.brand};
  border-radius: 2px;
  transition: width 0.3s ease;
`

// --- Component ---

export function ClustersView() {
  let projectId = null
  let project = null
  let clusterList = []
  let stats = null
  let config = null
  let loading = false
  let embedding = false
  let clustering = false
  let clusterK = 10
  let error = null

  // Config form
  let configProvider = 'openai'
  let configModel = 'text-embedding-3-small'
  let configApiKey = ''
  let configBaseUrl = ''
  let showConfig = false
  let embedProgress = null // { phase, completed, total, skipped }
  let unsubProgress = null
  let distinctRoles = [] // all role values found in messages
  let configUserRoles = new Set()    // roles the user marks as "user"
  let configAssistantRoles = new Set() // roles the user marks as "assistant"
  let isOwner = false

  async function loadData() {
    loading = true
    m.redraw()
    try {
      const proj = await projectsApi.get(projectId)
      project = proj
      isOwner = proj.memberRole === 'owner'
      ;[clusterList, stats, config, distinctRoles] = await Promise.all([
        clustersApi.list(projectId),
        embeddingsApi.getStats(projectId),
        embeddingsApi.getConfig(projectId),
        embeddingsApi.getRoles(projectId),
      ])
      if (config && config.provider) {
        configProvider = config.provider
        configModel = config.model
        configBaseUrl = config.base_url || ''
        configUserRoles = new Set(config.user_roles || [])
        configAssistantRoles = new Set(config.assistant_roles || [])
      }
    } catch (err) {
      console.error('Failed to load cluster data:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function saveConfig() {
    try {
      error = null
      const data = { provider: configProvider, model: configModel }
      if (configProvider === 'custom') data.baseUrl = configBaseUrl
      if (configApiKey) data.apiKey = configApiKey
      if (configUserRoles.size > 0) data.userRoles = [...configUserRoles]
      if (configAssistantRoles.size > 0) data.assistantRoles = [...configAssistantRoles]
      config = await embeddingsApi.saveConfig(projectId, data)
      configApiKey = ''
      showConfig = false
      m.redraw()
    } catch (err) {
      error = err.message
      m.redraw()
    }
  }

  async function embedAll() {
    embedding = true
    embedProgress = null
    error = null
    m.redraw()
    try {
      await embeddingsApi.embedAll(projectId)
      await embeddingsApi.embedPairs(projectId)
      stats = await embeddingsApi.getStats(projectId)
    } catch (err) {
      error = err.message
    } finally {
      embedding = false
      embedProgress = null
      m.redraw()
    }
  }

  async function clearEmbeddings() {
    if (!confirm('Clear all embeddings and clusters? You will need to re-embed.')) return
    error = null
    try {
      await embeddingsApi.clear(projectId)
      ;[stats, clusterList] = await Promise.all([
        embeddingsApi.getStats(projectId),
        clustersApi.list(projectId),
      ])
    } catch (err) {
      error = err.message
    }
    m.redraw()
  }

  async function runClustering() {
    clustering = true
    error = null
    m.redraw()
    try {
      await clustersApi.run(projectId, parseInt(clusterK) || 10)
      ;[clusterList, stats] = await Promise.all([
        clustersApi.list(projectId),
        embeddingsApi.getStats(projectId),
      ])
    } catch (err) {
      error = err.message
    } finally {
      clustering = false
      m.redraw()
    }
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      loadData()
      realtime.connect(projectId)
      unsubProgress = realtime.on('embedding_progress', (data) => {
        embedProgress = data
      })
    },

    onremove() {
      if (unsubProgress) unsubProgress()
      realtime.disconnect()
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      const isConfigured = config && config.provider

      return m('div', { class: pageWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Clusters',
        ]),

        m('div', { class: headerRow }, [
          m('h1', { style: `font-size: ${font.size['2xl']}; font-weight: ${font.weight.bold};` },
            'Clustering & Similarity'),
          isOwner ? m('div', { style: 'display: flex; gap: 8px;' }, [
            m('button', {
              class: btnSecondary,
              onclick: () => { showConfig = !showConfig; m.redraw() },
            }, showConfig ? 'Hide Config' : 'Settings'),
          ]) : null,
        ]),

        error ? m('div', {
          style: `padding: ${space.md}; background: #fff0f0; border: 1px solid #ffcccc; border-radius: 6px; color: #cc0000; font-size: ${font.size.sm};`,
        }, error) : null,

        // Stats bar
        stats ? m('div', { class: statsBar }, [
          m('div', { class: statItem }, [
            m('div', { class: statLabel }, 'Total Sessions'),
            m('div', { class: statValue }, stats.totalSessions),
          ]),
          m('div', { class: statItem }, [
            m('div', { class: statLabel }, 'Embedded'),
            m('div', { class: statValue }, stats.embeddedSessions),
          ]),
          m('div', { class: statItem }, [
            m('div', { class: statLabel }, 'Q&A Pairs'),
            m('div', { class: statValue }, stats.embeddedPairs),
          ]),
          m('div', { class: statItem }, [
            m('div', { class: statLabel }, 'Clusters'),
            m('div', { class: statValue }, stats.clusterCount),
          ]),
          stats.totalSessions > 0 ? m('div', { class: statItem }, [
            m('div', { class: statLabel }, 'Coverage'),
            m('div', { class: statValue },
              `${Math.round((stats.embeddedSessions / stats.totalSessions) * 100)}%`),
            m('div', { class: progressBar },
              m('div', {
                class: progressFill,
                style: `width: ${(stats.embeddedSessions / stats.totalSessions) * 100}%`,
              })
            ),
          ]) : null,
        ]) : null,

        // Config panel (owner only)
        showConfig && isOwner ? m('div', { class: configPanel }, [
          m('h3', { style: `font-size: ${font.size.lg}; margin-bottom: ${space.sm};` },
            'Embedding Configuration'),
          m('div', { class: inlineForm }, [
            m('label', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; min-width: 60px;` }, 'Provider'),
            m('select', {
              style: `padding: 6px 10px; border: 1px solid ${colors.border}; border-radius: 6px; font-family: ${font.family};`,
              value: configProvider,
              onchange: (e) => { configProvider = e.target.value },
            }, [
              m('option', { value: 'openai' }, 'OpenAI'),
              m('option', { value: 'custom' }, 'Custom / LiteLLM'),
            ]),
          ]),
          configProvider === 'custom'
            ? m('div', { class: inlineForm }, [
                m('label', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; min-width: 60px;` }, 'Base URL'),
                m('input', {
                  class: input,
                  style: 'max-width: 400px;',
                  placeholder: 'https://your-litellm-proxy.com/v1/embeddings',
                  value: configBaseUrl,
                  oninput: (e) => { configBaseUrl = e.target.value },
                }),
              ])
            : null,
          m('div', { class: inlineForm }, [
            m('label', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; min-width: 60px;` }, 'Model'),
            m('input', {
              class: input,
              style: 'max-width: 300px;',
              value: configModel,
              oninput: (e) => { configModel = e.target.value },
            }),
          ]),
          m('div', { class: inlineForm }, [
            m('label', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; min-width: 60px;` }, 'API Key'),
            m('input', {
              class: input,
              style: 'max-width: 300px;',
              type: 'password',
              placeholder: isConfigured && config.hasApiKey ? '(key saved)' : 'Enter API key',
              value: configApiKey,
              oninput: (e) => { configApiKey = e.target.value },
            }),
          ]),
          distinctRoles.length > 0 ? m('div', {
            style: `margin-top: ${space.sm}; padding: ${space.md}; background: ${colors.bgSidebar}; border-radius: 6px;`,
          }, [
            m('div', {
              style: `font-size: ${font.size.sm}; font-weight: ${font.weight.medium}; margin-bottom: ${space.sm};`,
            }, 'Role Mapping (for Q&A pair detection)'),
            m('div', {
              style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; margin-bottom: ${space.sm};`,
            }, 'Select which roles represent the user and the assistant in your data.'),
            m('table', {
              style: `font-size: ${font.size.sm}; border-collapse: collapse; width: auto;`,
            }, [
              m('thead', m('tr', [
                m('th', { style: `text-align: left; padding: 4px 12px 4px 0; color: ${colors.textMuted}; font-weight: ${font.weight.medium};` }, 'Role'),
                m('th', { style: `text-align: center; padding: 4px 12px; color: ${colors.textMuted}; font-weight: ${font.weight.medium};` }, 'User'),
                m('th', { style: `text-align: center; padding: 4px 12px; color: ${colors.textMuted}; font-weight: ${font.weight.medium};` }, 'Assistant'),
              ])),
              m('tbody', distinctRoles.map(role =>
                m('tr', { key: role }, [
                  m('td', { style: 'padding: 3px 12px 3px 0; font-family: monospace;' }, role),
                  m('td', { style: 'text-align: center; padding: 3px 12px;' },
                    m('input', {
                      type: 'checkbox',
                      checked: configUserRoles.has(role),
                      onchange: () => {
                        if (configUserRoles.has(role)) configUserRoles.delete(role)
                        else configUserRoles.add(role)
                        configAssistantRoles.delete(role) // can't be both
                      },
                    })
                  ),
                  m('td', { style: 'text-align: center; padding: 3px 12px;' },
                    m('input', {
                      type: 'checkbox',
                      checked: configAssistantRoles.has(role),
                      onchange: () => {
                        if (configAssistantRoles.has(role)) configAssistantRoles.delete(role)
                        else configAssistantRoles.add(role)
                        configUserRoles.delete(role) // can't be both
                      },
                    })
                  ),
                ])
              )),
            ]),
          ]) : null,
          m('div', { style: 'display: flex; gap: 8px;' }, [
            m('button', { class: btnPrimary, onclick: saveConfig }, 'Save Configuration'),
          ]),
        ]) : null,

        // Action buttons
        isConfigured ? m('div', { style: 'display: flex; gap: 8px; align-items: center; flex-wrap: wrap;' }, [
          isOwner ? m('button', {
            class: btnPrimary,
            disabled: embedding,
            onclick: embedAll,
          }, embedding ? 'Embedding...' : 'Embed All Sessions') : null,
          isOwner && embedding && embedProgress ? m('div', {
            style: `display: flex; align-items: center; gap: ${space.sm}; min-width: 220px;`,
          }, [
            m('div', { style: 'flex: 1; min-width: 120px;' }, [
              m('div', { class: progressBar, style: 'margin-top: 0;' },
                m('div', {
                  class: progressFill,
                  style: `width: ${embedProgress.total > 0 ? (embedProgress.completed / embedProgress.total) * 100 : 0}%`,
                })
              ),
            ]),
            m('span', {
              style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; white-space: nowrap;`,
            }, embedProgress.phase === 'sessions'
              ? `Sessions: ${embedProgress.completed}/${embedProgress.total}`
              : `Q&A pairs: ${embedProgress.completed}/${embedProgress.total}`
            ),
          ]) : null,
          isOwner && stats && stats.embeddedSessions > 0 ? m('button', {
            class: btnSecondary,
            disabled: embedding,
            onclick: clearEmbeddings,
            style: `color: ${colors.textMuted};`,
          }, 'Clear Embeddings') : null,
          m('div', { class: inlineForm }, [
            m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` }, 'Clusters'),
            m('input', {
              class: smallInput,
              type: 'number',
              min: 2,
              max: 100,
              value: clusterK,
              oninput: (e) => { clusterK = e.target.value },
            }),
            m('button', {
              class: btnSecondary,
              disabled: clustering || !stats || stats.embeddedPairs < 2,
              onclick: runClustering,
            }, clustering ? 'Clustering...' : 'Run Clustering'),
          ]),
        ]) : isOwner ? m('div', { class: emptyState },
          'Configure an embedding provider in Settings to enable clustering and similarity.') : null,

        // Cluster grid
        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : clusterList.length > 0
            ? m('div', { class: clusterGrid },
                clusterList.map(cluster =>
                  m('div', {
                    key: cluster.id,
                    class: clusterCard,
                    onclick: () => m.route.set(`/projects/${projectId}/clusters/${cluster.id}`),
                  }, [
                    m('div', { class: clusterLabel }, cluster.label || 'Unlabeled cluster'),
                    m('div', { class: clusterMeta },
                      `${cluster.pair_count} pairs` + (cluster.session_count ? ` from ${cluster.session_count} sessions` : '')),
                    cluster.topTags && cluster.topTags.length > 0
                      ? m('div', { class: tagChips },
                          cluster.topTags.map(t =>
                            m('span', {
                              key: t.id,
                              class: tagChip,
                              style: `background: ${t.color || colors.border}; color: ${colors.text};`,
                            }, `${t.name} (${t.count})`)
                          )
                        )
                      : null,
                  ])
                )
              )
            : isConfigured && stats && stats.clusterCount === 0 && stats.embeddedPairs > 0
              ? m('div', { class: emptyState }, 'Q&A pairs embedded. Run clustering to group them.')
              : null,
      ])
    },
  }
}
